import { CheckCircle2, Target, Compass, Award, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { PageHeader } from "../components/shared/PageHeader";
import { CTASection } from "../components/shared/CTASection";
import { IMAGES, COMPANY, TEAM } from "../data/content";

const VALUES = [
  { icon: Target, title: "Safety as strategy", desc: "We treat safety and compliance as the foundation for growth, not a constraint on it." },
  { icon: Compass, title: "Global orientation", desc: "A worldwide lens with deep sensitivity to regional regulation and market context." },
  { icon: Award, title: "Credible outcomes", desc: "Practical, auditable recommendations that hold up under inspection and scale." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        testId="about-header"
        eyebrow="About Us"
        title="A specialist aviation division, backed by a broader company."
        subtitle={`${COMPANY.name} is a division of ${COMPANY.parent} — combining focused aviation expertise with the credibility and reach of an established parent organization.`}
        image={IMAGES.consulting}
      />

      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-aero">Who we are</span>
                <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl text-balance">
                  Advisory that spans safety, operations, security and AI transformation.
                </h2>
                <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground lg:text-lg">
                  <p>
                    We exist to help aviation organizations operate with confidence. Whether you are
                    an airline preparing for audit, an MRO scaling operations, a startup building
                    compliant foundations, or a government body advancing capability, we bring
                    structure, rigor and a forward-facing perspective.
                  </p>
                  <p>
                    Our positioning as a division of {COMPANY.parent} gives clients the best of both
                    worlds: a specialized, aviation-facing brand supported by the depth and
                    credibility of a broader management company — with room to expand our services
                    as your needs grow.
                  </p>
                </div>
                <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                  {["Commercial & public-sector experience", "Global reach, Africa growth focus", "Responsible AI adoption", "Documentation & audit expertise"].map((t) => (
                    <li key={t} className="flex items-center gap-3 text-sm font-medium text-navy">
                      <CheckCircle2 className="h-5 w-5 shrink-0 text-brand" /> {t}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <img src={IMAGES.jetTarmac} alt="Business jet on tarmac" className="h-96 w-full rounded-3xl object-cover lg:h-[520px]" />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-secondary py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <h2 className="max-w-2xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              What guides our work
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-border bg-white p-8 transition-transform hover:-translate-y-1 hover:shadow-xl">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-brand/10 text-brand">
                    <v.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-display text-lg font-bold text-navy">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-team-preview" className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="overflow-hidden rounded-3xl border border-border bg-white shadow-sm">
                  <div className="aspect-[4/5] w-full">
                    {TEAM.leader.photo ? (
                      <img src={TEAM.leader.photo} alt={TEAM.leader.name} className="h-full w-full object-cover object-top" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand to-navy">
                        <span className="font-display text-5xl font-extrabold text-white/90">{TEAM.leader.initials}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="font-display text-xl font-extrabold text-navy">{TEAM.leader.name}</div>
                    <div className="mt-1 text-sm font-semibold uppercase tracking-wide text-aero">{TEAM.leader.title}</div>
                  </div>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-aero">Our Team</span>
                <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl text-balance">
                  Experienced leadership, backed by specialist advisors.
                </h2>
                <p className="mt-6 text-base leading-relaxed text-muted-foreground lg:text-lg">
                  {TEAM.networkNote}
                </p>
                <Link to="/team" data-testid="about-team-cta" className="mt-8 inline-flex items-center gap-1.5 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
                  Meet the team <ArrowUpRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
